// apps/web/src/features/cargo/mutations.ts
//
// TanStack write hooks for the Cargo registry workflows (#1232). Every
// successful registry mutation invalidates BOTH the list (`['cargo','infinite']`)
// AND the summary (`['cargo','summary']`) families so totals/indicators stay in
// step with the rows. Invalidation (not reset) preserves the caller's
// filter/page context. Toasts are raised by the calling component, not here, so
// the same mutation can be reused with surface-specific copy.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CargoArticleWire,
  CargoClientPaymentReviewBody,
  CargoClientPaymentSubmitBody,
  CargoImportRequest,
  CargoImportResponse,
  CargoPaymentDocumentCreateInput,
  CargoPaymentDocumentListResponse,
  CargoPaymentDocumentWire,
  CreateCargoArticleInput,
  UpdateCargoArticleInput,
} from '@magimp/shared';

import {
  createCargo,
  deleteCargo,
  importCargo,
  reviewCargoClientPayment,
  submitCargoClientPayment,
  updateCargo,
} from '../../api/cargo-write.js';
import {
  createCargoPaymentDocument,
  deleteCargoPaymentDocument,
  listCargoPaymentDocuments,
} from '../../api/cargo-payment-docs.js';
import { cargoKeys } from './queries.js';

/** Invalidate both the row list and the totals/indicators summary. */
function useInvalidateCargo(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: cargoKeys.all });
  };
}

export function useCreateCargo() {
  const invalidate = useInvalidateCargo();
  return useMutation<CargoArticleWire, Error, CreateCargoArticleInput>({
    mutationFn: (input) => createCargo(input),
    onSuccess: invalidate,
  });
}

export function useUpdateCargo(id: string) {
  const invalidate = useInvalidateCargo();
  return useMutation<CargoArticleWire, Error, UpdateCargoArticleInput>({
    mutationFn: (patch) => updateCargo(id, patch),
    onSuccess: invalidate,
  });
}

export function useDeleteCargo() {
  const invalidate = useInvalidateCargo();
  return useMutation<void, Error, string>({
    mutationFn: (id) => deleteCargo(id),
    onSuccess: invalidate,
  });
}

export function useImportCargo() {
  const invalidate = useInvalidateCargo();
  return useMutation<CargoImportResponse, Error, CargoImportRequest>({
    mutationFn: (body) => importCargo(body),
    onSuccess: invalidate,
  });
}

// ── payment documents ───────────────────────────────────────────────────────

export function useCargoPaymentDocuments(cargoId: string, enabled: boolean) {
  return useQuery<CargoPaymentDocumentListResponse>({
    queryKey: cargoKeys.paymentDocuments(cargoId),
    queryFn: ({ signal }) => listCargoPaymentDocuments(cargoId, { signal }),
    enabled,
  });
}

function useInvalidatePaymentDocuments(cargoId: string): () => void {
  const qc = useQueryClient();
  const invalidate = useInvalidateCargo();
  return () => {
    void qc.invalidateQueries({ queryKey: cargoKeys.paymentDocuments(cargoId) });
    // A first/last document can flip the gated-save availability and the row's
    // payment indicators, so refresh the registry too.
    invalidate();
  };
}

export function useCreateCargoPaymentDocument(cargoId: string) {
  const invalidate = useInvalidatePaymentDocuments(cargoId);
  return useMutation<CargoPaymentDocumentWire, Error, CargoPaymentDocumentCreateInput>({
    mutationFn: (input) => createCargoPaymentDocument(cargoId, input),
    onSuccess: invalidate,
  });
}

export function useDeleteCargoPaymentDocument(cargoId: string) {
  const invalidate = useInvalidatePaymentDocuments(cargoId);
  return useMutation<void, Error, string>({
    mutationFn: (documentId) => deleteCargoPaymentDocument(cargoId, documentId),
    onSuccess: invalidate,
  });
}

// ── client-payment workflow (#1936/#1938) ───────────────────────────────────
//
// Both reuse `useInvalidatePaymentDocuments`: a submit/review flips per-document
// review state AND the row's payment status/indicators, so the docs query and
// the registry both refresh.

/** Manager submit: `PATCH /cargo/:id/client-payment-status` with `{ requestedStatus }`. */
export function useSubmitCargoClientPayment(cargoId: string) {
  const invalidate = useInvalidatePaymentDocuments(cargoId);
  return useMutation<void, Error, CargoClientPaymentSubmitBody['requestedStatus']>({
    mutationFn: (requestedStatus) => submitCargoClientPayment(cargoId, requestedStatus),
    onSuccess: invalidate,
  });
}

/** Financier review: `PATCH /cargo/:id/client-payment-review` with `{ decisions }`. */
export function useReviewCargoClientPayment(cargoId: string) {
  const invalidate = useInvalidatePaymentDocuments(cargoId);
  return useMutation<void, Error, CargoClientPaymentReviewBody>({
    mutationFn: (body) => reviewCargoClientPayment(cargoId, body),
    onSuccess: invalidate,
  });
}
